import React from 'react';
import { Box } from '@mui/material';

import { Typography } from '@occc/ui-design-system';
import { ProductGroupFragment as ProductGroup } from '@occc/graphql';

import { State as EarningMechanismState } from '../../EarningMechanisms/redux';
import { Ul } from '../../../components/Ul';

const externalFulfillmentSummary = (): JSX.Element => (
  <Typography paragraph>
    A membership must engage in a behavior that is verified by one of our systems or a third-party
    system, but that <b>is not controlled by Activate</b>. When that system verifies the behavior,
    it will send a "fulfilled_offer_id" that matches the offer id I'll get as soon as this offer is
    successfully submitted.
  </Typography>
);

const loyaltyEventSummary = (
  loyaltyEvent: EarningMechanismState['emConfiguration']['loyaltyEvent'],
): JSX.Element => (
  <Typography paragraph>
    A membership will engage in a loyalty-behavior that we've defined, either at one of our
    locations or via our mobile app. When they do, we'll create a new "LoyaltyEvent" in Activate
    with a payload whose "event_type" is
    <b>{loyaltyEvent.eventType}</b>.
  </Typography>
);

const membershipEventSummary = (
  membershipEvent: EarningMechanismState['emConfiguration']['membershipEvent'],
): JSX.Element => {
  switch (membershipEvent.eventType) {
    case 'birthday':
      return (
        <Typography paragraph>
          We want a membership to be surprised by our loyalty program on their
          <b>birthday</b> so we'll surprise them with a free gift.
        </Typography>
      );
    case 'active':
      return (
        <Typography paragraph>
          We want to incentivize the member to confirm their membership by confirming their email or
          phone number (via SMS). Once they do, we'll give them a free gift.
        </Typography>
      );
    case 'created':
      return (
        <Typography paragraph>
          We want the membership to be able to engage with our loyalty program as soon as they sign
          up, so we'll give them something they can use right away.
        </Typography>
      );
  }
};

const perGroupPurchasedSummary = (
  perGroupPurchased: EarningMechanismState['emConfiguration']['perGroupPurchased'],
): JSX.Element => {
  const groups = perGroupPurchased.groups.map((group) => {
    const { countPer, countThreshold, groupIndex, itemKey, summationMode, values } = group;
    const countPerCopy = countPer === 'quantity' ? 'items' : 'dollars worth of items';
    const summationModeCopy = summationMode === 'include' ? 'that match' : 'that do not match';

    return (
      <li key={groupIndex}>
        <b>{countThreshold}</b> <b>{countPerCopy}</b> <b>{summationModeCopy}</b> the line item
        property <b>{itemKey}</b> with the following values: <b>{values.join(', ')}</b>.
      </li>
    );
  });

  const multiplePurchases = perGroupPurchased.groups.map((group) => {
    const summationMode = group.summationMode === 'include' ? 'of' : 'of anything except';
    return `${2 * group.countThreshold} ${summationMode} ${group.itemKey} ${group.values.join(
      ', ',
    )}`;
  });

  return (
    <Typography paragraph>
      A membership must buy a few items all at once. They're identified like this:
      <Ul>{groups}</Ul>
      <br />
      Note: If a membership purchases enough to satisfy all of the item thresholds multiple times,
      e.g., ({multiplePurchases.join(' and ')}
      ), they'll earn multiple awards. Each award will count as an <b>individual fulfillment</b> of
      the offer.
    </Typography>
  );
};

const perItemPurchasedSummary = (
  perItemPurchased: EarningMechanismState['emConfiguration']['perItemPurchased'],
): JSX.Element => {
  const countPer = perItemPurchased.countPer === 'quantity' ? 'items' : 'dollars worth of items';
  const summationMode =
    perItemPurchased.summationMode === 'include' ? 'that match' : 'that do not match';
  const rounding = {
    half_up: 'round the sum to the nearest whole number',
    down: 'round the sum strictly down',
  }[perItemPurchased.countRounding];

  return (
    <Typography paragraph>
      We'd like to promote a specific product to our membership base. We've identified it as any
      amount of <b>{countPer}</b> <b>{summationMode}</b> the line item property{' '}
      <b>{perItemPurchased.itemKey}</b> with the following values:{' '}
      <b>{perItemPurchased.values.join(', ')}</b>.<br />
      We'd like to {rounding} before calculating the award.
    </Typography>
  );
};

const perItemsAccumulatedSummary = (
  perItemsAccumulated: EarningMechanismState['emConfiguration']['perItemsAccumulated'],
): JSX.Element => {
  const countPer = perItemsAccumulated.countPer === 'quantity' ? 'items' : 'dollars worth of items';
  const summationMode =
    perItemsAccumulated.summationMode === 'include' ? 'that match' : 'that do not match';

  return (
    <Typography paragraph>
      We'd like to promote a specific product that a membership can "save up" over time in order to
      earn an award. We've identified the item as any amount of <b>{countPer}</b>{' '}
      <b>{summationMode}</b> the line item property
      <b>{perItemsAccumulated.itemKey}</b> with the following values:{' '}
      <b>{perItemsAccumulated.values.join(', ')}</b>.<br />
      The membership must accumulate <b>{perItemsAccumulated.countThreshold}</b> within the lifetime
      of the offer in order to receive the award.
    </Typography>
  );
};

const perTaggedGroupPurchasedSummary = (
  perTaggedGroupPurchased: EarningMechanismState['emConfiguration']['perTaggedGroupPurchased'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const groups = perTaggedGroupPurchased.groups.map((group) => {
    const { countPer, countThreshold, groupIndex, summationMode, tags } = group;
    const countPerCopy = countPer === 'quantity' ? 'items' : 'dollars worth of items';
    const summationModeCopy = summationMode === 'include' ? 'that are' : 'that are not';

    const selectedProductGroupNames = productGroups
      .filter((t) => tags.includes(t.id))
      .map((t) => t.name)
      .join(', ');

    return (
      <li key={groupIndex}>
        <b>{countThreshold}</b> <b>{countPerCopy}</b> <b>{summationModeCopy}</b> in the product
        group(s): <b>{selectedProductGroupNames}</b>.
      </li>
    );
  });

  const multiplePurchases = perTaggedGroupPurchased.groups.map((group) => {
    const summationMode = group.summationMode === 'include' ? 'of' : 'of anything except';
    const selectedProductGroupNames = productGroups
      .filter((t) => group.tags.includes(t.id))
      .map((t) => t.name)
      .join(', ');
    return `${2 * group.countThreshold} ${summationMode} ${selectedProductGroupNames}`;
  });

  return (
    <Box>
      <Typography paragraph>
        A membership must buy a few items all at once. They're identified like this:
      </Typography>

      <Ul>{groups}</Ul>
      <Typography paragraph>
        <br />
        Note: If a membership purchases enough to satisfy all of the item thresholds multiple times,
        e.g., ({multiplePurchases.join(' and ')}
        ), they'll earn multiple awards. Each award will count as an <b>
          individual fulfillment
        </b>{' '}
        of the offer.
      </Typography>
    </Box>
  );
};

const perTaggedItemPurchasedSummary = (
  perItemPurchased: EarningMechanismState['emConfiguration']['perTaggedItemPurchased'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const summationMode = perItemPurchased.summationMode === 'include' ? 'that is' : 'that is not';
  const rounding = {
    half_up: 'round the sum to the nearest whole number',
    down: 'round the sum strictly down',
  }[perItemPurchased.countRounding];

  const selectedProductGroupNames = productGroups
    .filter((t) => perItemPurchased.tags.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return (
    <Typography paragraph>
      We'd like to promote a specific product to our membership base. We've identified it as a set{' '}
      <b>{summationMode}</b> in the product group(s): <b>{selectedProductGroupNames}</b>.<br />
      We'd like to {rounding} before calculating the award.
    </Typography>
  );
};

const perTaggedItemsAccumulatedSummary = (
  perTaggedItemsAccumulated: EarningMechanismState['emConfiguration']['perTaggedItemsAccumulated'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const countPer =
    perTaggedItemsAccumulated.countPer === 'quantity' ? 'items' : 'dollars worth of items';
  const summationMode =
    perTaggedItemsAccumulated.summationMode === 'include' ? 'that match' : 'that do not match';

  const selectedProductGroupNames = productGroups
    .filter((t) => perTaggedItemsAccumulated.tags.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return (
    <Typography paragraph>
      We'd like to promote a specific product that a membership can "save up" over time in order to
      earn an award. We've identified the item as any amount of <b>{countPer}</b>{' '}
      <b>{summationMode}</b> in the product group(s): <b>{selectedProductGroupNames}</b>.<br />
      The membership must accumulate <b>{perTaggedItemsAccumulated.countThreshold}</b> within the
      lifetime of the offer in order to receive the award.
    </Typography>
  );
};

const productPurchaseSummary = (
  productPurchase: EarningMechanismState['emConfiguration']['productPurchase'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const { countProductsBy, eligibleProductGroups, purchaseThreshold } = productPurchase;
  const countProductsByCopy = countProductsBy === 'quantity' ? 'items' : 'dollars worth of items';
  const summationModeCopy = 'that are';
  const selectedProductGroupNames = productGroups
    .filter((t) => eligibleProductGroups.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  const summationMode = 'of';
  const multiplePurchasesCopy = `${
    2 * purchaseThreshold
  } ${summationMode} ${selectedProductGroupNames}`;

  return (
    <Box>
      <Typography paragraph>
        A membership must buy a few items all at once over a series of transactions. They're
        identified like this:
        <br />
        <b>{purchaseThreshold}</b> <b>{countProductsByCopy}</b> <b>{summationModeCopy}</b> in the
        product group(s): <b>{selectedProductGroupNames}</b>.<br />
        Note: If a membership purchases enough to satisfy all of the item thresholds multiple times,
        e.g., ({multiplePurchasesCopy}
        ), they'll earn multiple awards. Each award will count as an <b>
          individual fulfillment
        </b>{' '}
        of the offer.
      </Typography>
    </Box>
  );
};

const totalEligiblePurchaseSummary = (
  totalEligiblePurchase: EarningMechanismState['emConfiguration']['totalEligiblePurchase'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const rounding = {
    half_up: 'round the sum to the nearest whole number',
    down: 'round the sum strictly down',
  }[totalEligiblePurchase.roundCountedProducts];

  const selectedProductGroupNames = productGroups
    .filter((t) => totalEligiblePurchase.eligibleProductGroups.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return (
    <Typography paragraph>
      We'd like to promote a specific product to our membership base. We've identified it as a set{' '}
      <b>that is</b> in the product group(s): <b>{selectedProductGroupNames}</b>.<br />
      We'd like to {rounding} before calculating the award.
    </Typography>
  );
};

const totalEligiblePurchaseOverTimeSummary = (
  totalEligiblePurchaseOverTime: EarningMechanismState['emConfiguration']['totalEligiblePurchaseOverTime'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const countPer =
    totalEligiblePurchaseOverTime.countProductsBy === 'quantity'
      ? 'items'
      : 'dollars worth of items';

  const selectedProductGroupNames = productGroups
    .filter((t) => totalEligiblePurchaseOverTime.eligibleProductGroups.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return (
    <Typography paragraph>
      We'd like to promote a specific product that a membership can "save up" over time in order to
      earn an award. We've identified the item as any amount of <b>{countPer}</b> <b>that match</b>{' '}
      in the product group(s): <b>{selectedProductGroupNames}</b>.<br />
      The membership must accumulate <b>
        {totalEligiblePurchaseOverTime.accumulationThreshold}
      </b>{' '}
      within the lifetime of the offer in order to receive the award.
    </Typography>
  );
};

const totalSpendOnTransactionSummary = (
  totalSpendOnTransaction: EarningMechanismState['emConfiguration']['totalSpendOnTransaction'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const rounding = {
    half_up: 'round the sum to the nearest whole number',
    down: 'round the sum strictly down',
    raw: 'do not round the sum',
  }[totalSpendOnTransaction.roundMoneyAmount];

  const selectedProductGroupNames = productGroups
    .filter((t) => totalSpendOnTransaction.ineligibleProductGroups.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return selectedProductGroupNames ? (
    <Typography paragraph>
      We'd like to promote all products to our membership base. We've identified any{' '}
      <b>money amount spent</b> on items will apply <b>as long as they do not</b> belong to the
      product group(s): <b>{selectedProductGroupNames}</b>.<br />
      We'd like to {rounding} before calculating the award.
    </Typography>
  ) : (
    <Typography paragraph>
      We'd like to promote all products to our membership base. We've identified any{' '}
      <b>money amount spent</b> on items will apply.
      <br />
      We'd like to {rounding} before calculating the award.
    </Typography>
  );
};

const totalSpendOverTimeSummary = (
  totalSpendOverTime: EarningMechanismState['emConfiguration']['totalSpendOverTime'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const selectedProductGroupNames = productGroups
    .filter((t) => totalSpendOverTime.ineligibleProductGroups.includes(t.id))
    .map((t) => t.name)
    .join(', ');

  return selectedProductGroupNames ? (
    <Typography paragraph>
      We'd like to promote all products that a membership can "save up" over time in order to earn
      an award. We've identified any <b>money amount spent</b> on items will apply{' '}
      <b>as long as they do not</b> belong to the product group(s):{' '}
      <b>{selectedProductGroupNames}</b>.<br />
      The membership must accumulate <b>{totalSpendOverTime.accumulationThreshold}</b> within the
      lifetime of the offer in order to receive the award.
    </Typography>
  ) : (
    <Typography paragraph>
      We'd like to promote all products that a membership can "save up" over time in order to earn
      an award. We've identified any <b>money amount spent</b> on items will apply.
      <br />
      The membership must accumulate <b>{totalSpendOverTime.accumulationThreshold}</b> within the
      lifetime of the offer in order to receive the award.
    </Typography>
  );
};

const varietyPurchaseSummary = (
  varietyPurchase: EarningMechanismState['emConfiguration']['varietyPurchase'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const products = varietyPurchase.products.map((product) => {
    const { countProductsBy, eligibleProductGroups, productIndex, purchaseThreshold } = product;
    const countProductsByCopy = countProductsBy === 'quantity' ? 'items' : 'dollars worth of items';
    const summationModeCopy = 'that are';

    const selectedProductGroupNames = productGroups
      .filter((t) => eligibleProductGroups.includes(t.id))
      .map((t) => t.name)
      .join(', ');

    return (
      <li key={productIndex}>
        <b>{purchaseThreshold}</b> <b>{countProductsByCopy}</b> <b>{summationModeCopy}</b> in the
        product group(s): <b>{selectedProductGroupNames}</b>.
      </li>
    );
  });

  const multiplePurchases = varietyPurchase.products.map((product) => {
    const summationMode = 'of';
    const selectedProductGroupNames = productGroups
      .filter((t) => product.eligibleProductGroups.includes(t.id))
      .map((t) => t.name)
      .join(', ');
    return `${2 * product.purchaseThreshold} ${summationMode} ${selectedProductGroupNames}`;
  });

  return (
    <Box>
      <Typography paragraph>
        A membership must buy a few items all at once in a single transaction. They're identified
        like this:
      </Typography>

      <Ul>{products}</Ul>
      <Typography paragraph>
        <br />
        Note: If a membership purchases enough to satisfy all of the item thresholds multiple times,
        e.g., ({multiplePurchases.join(' and ')}
        ), they'll earn multiple awards. Each award will count as an <b>
          individual fulfillment
        </b>{' '}
        of the offer.
      </Typography>
    </Box>
  );
};

const varietyPurchaseOverTimeSummary = (
  varietyPurchaseOverTime: EarningMechanismState['emConfiguration']['varietyPurchaseOverTime'],
  productGroups: ProductGroup[],
): JSX.Element => {
  const products = varietyPurchaseOverTime.products.map((product) => {
    const { countProductsBy, eligibleProductGroups, productIndex, purchaseThreshold } = product;
    const countProductsByCopy = countProductsBy === 'quantity' ? 'items' : 'dollars worth of items';
    const summationModeCopy = 'that are';

    const selectedProductGroupNames = productGroups
      .filter((t) => eligibleProductGroups.includes(t.id))
      .map((t) => t.name)
      .join(', ');

    return (
      <li key={productIndex}>
        <b>{purchaseThreshold}</b> <b>{countProductsByCopy}</b> <b>{summationModeCopy}</b> in the
        product group(s): <b>{selectedProductGroupNames}</b>.
      </li>
    );
  });

  const multiplePurchases = varietyPurchaseOverTime.products.map((product) => {
    const summationMode = 'of';
    const selectedProductGroupNames = productGroups
      .filter((t) => product.eligibleProductGroups.includes(t.id))
      .map((t) => t.name)
      .join(', ');
    return `${2 * product.purchaseThreshold} ${summationMode} ${selectedProductGroupNames}`;
  });

  return (
    <Box>
      <Typography paragraph>
        A membership must buy a few items all at once over a series of transactions. They're
        identified like this:
      </Typography>

      <Ul>{products}</Ul>
      <Typography paragraph>
        <br />
        Note: If a membership purchases enough to satisfy all of the item thresholds multiple times,
        e.g., ({multiplePurchases.join(' and ')}
        ), they'll earn multiple awards. Each award will count as an <b>
          individual fulfillment
        </b>{' '}
        of the offer.
      </Typography>
    </Box>
  );
};

const visitsSummary = (visits: EarningMechanismState['emConfiguration']['visits']): JSX.Element => (
  <Typography paragraph>
    We'd like to incentivize memberships to visit more frequently by rewarding them after they visit{' '}
    <b>{visits.visitThreshold}</b> times.
  </Typography>
);

type EarningMechanismSummary = (
  selectedEarningMechanism: EarningMechanismState['selectedEarningMechanism'],
  emConfiguration: EarningMechanismState['emConfiguration'],
  productGroups: ProductGroup[],
) => JSX.Element;
const earningMechanismSummary: EarningMechanismSummary = (
  selectedEarningMechanism,
  emConfiguration,
  productGroups,
) => {
  switch (selectedEarningMechanism) {
    case 'totalEligiblePurchaseOverTime':
      return totalEligiblePurchaseOverTimeSummary(
        emConfiguration.totalEligiblePurchaseOverTime,
        productGroups,
      );
    case 'totalEligiblePurchase':
      return totalEligiblePurchaseSummary(emConfiguration.totalEligiblePurchase, productGroups);
    case 'externalFulfillment':
      return externalFulfillmentSummary();
    case 'loyaltyEvent':
      return loyaltyEventSummary(emConfiguration.loyaltyEvent);
    case 'membershipEvent':
      return membershipEventSummary(emConfiguration.membershipEvent);
    case 'perGroupPurchased':
      return perGroupPurchasedSummary(emConfiguration.perGroupPurchased);
    case 'perItemPurchased':
      return perItemPurchasedSummary(emConfiguration.perItemPurchased);
    case 'perItemsAccumulated':
      return perItemsAccumulatedSummary(emConfiguration.perItemsAccumulated);
    case 'perTaggedGroupPurchased':
      return perTaggedGroupPurchasedSummary(emConfiguration.perTaggedGroupPurchased, productGroups);
    case 'perTaggedItemPurchased':
      return perTaggedItemPurchasedSummary(emConfiguration.perTaggedItemPurchased, productGroups);
    case 'perTaggedItemsAccumulated':
      return perTaggedItemsAccumulatedSummary(
        emConfiguration.perTaggedItemsAccumulated,
        productGroups,
      );
    case 'productPurchase':
      return productPurchaseSummary(emConfiguration.productPurchase, productGroups);
    case 'totalSpendOverTime':
      return totalSpendOverTimeSummary(emConfiguration.totalSpendOverTime, productGroups);
    case 'totalSpendOnTransaction':
      return totalSpendOnTransactionSummary(emConfiguration.totalSpendOnTransaction, productGroups);
    case 'varietyPurchase':
      return varietyPurchaseSummary(emConfiguration.varietyPurchase, productGroups);
    case 'varietyPurchaseOverTime':
      return varietyPurchaseOverTimeSummary(emConfiguration.varietyPurchaseOverTime, productGroups);
    case 'visits':
      return visitsSummary(emConfiguration.visits);
    case null:
      return <span>Error: No behavior has been configured.</span>;
  }
};

const externalPointAwardSummary = (
  externalPoint: EarningMechanismState['awardConfiguration']['externalPointAward'],
): JSX.Element => (
  <Typography paragraph>
    We plan to give the membership a number of points, but we'd like to decide how many to award
    when they exhibit the behavior. The point value might be generated by a partner that we work
    with, or via a mobile app, so we'll limit the number that a membership can receive from a single
    event to
    <b>{externalPoint.maximum}</b>.<br />
    <br />
    When we create the "LoyaltyEvent" in the Activate system, we'll include an "action" with{' '}
    <b>"add_points"</b> configured to the number of points we'd like to award in the payload.
  </Typography>
);

const pointAwardSummary = (
  point: EarningMechanismState['awardConfiguration']['pointAward'],
): JSX.Element => (
  <Typography paragraph>
    For each fulfillment generated by the membership as they engage with the offer, we'd like to
    award <b>{point.points}</b> points. Activate will take care of calculating and awarding these
    points based on the event that generated them.
  </Typography>
);

const pointsPerAwardSummary = (
  pointsPer: EarningMechanismState['awardConfiguration']['pointsPerAward'],
): JSX.Element => {
  const rounding = {
    half_up: 'to the nearest whole number',
    down: 'strictly down',
  }[pointsPer.pointRounding];

  return (
    <Typography paragraph>
      In order to entice the member to purchase the product(s) outlined above, we'll award{' '}
      <b>{pointsPer.points}</b> points multiplied by the sum that they purchased. In the case of
      fractional points, we'll round them {rounding}.
    </Typography>
  );
};

const nullAwardSummary = (): JSX.Element => (
  <Typography paragraph>
    Since this offer provides a non-point-based award in the form of a discount at the point of sale
    or free merchandise, we don't need Activate to provide any additional award.
  </Typography>
);

type AwardSummary = (
  selectedAward: EarningMechanismState['selectedAward'],
  awardConfiguration: EarningMechanismState['awardConfiguration'],
) => JSX.Element;
const awardSummary: AwardSummary = (selectedAward, awardConfiguration) => {
  switch (selectedAward) {
    case 'externalPointAward':
      return externalPointAwardSummary(awardConfiguration.externalPointAward);
    case 'pointAward':
      return pointAwardSummary(awardConfiguration.pointAward);
    case 'pointsPerAward':
      return pointsPerAwardSummary(awardConfiguration.pointsPerAward);
    case 'nullAward':
      return nullAwardSummary();
    case null:
      return <span>Error: No award has been configured.</span>;
  }
};

interface IProps {
  earningMechanisms: EarningMechanismState;
  productGroups: ProductGroup[];
}

export const EarningMechanismSummary: React.FC<IProps> = ({ earningMechanisms, productGroups }) => {
  const { awardConfiguration, emConfiguration, selectedEarningMechanism, selectedAward } =
    earningMechanisms;

  const earningMechanismSummaryComponent = earningMechanismSummary(
    selectedEarningMechanism,
    emConfiguration,
    productGroups,
  );
  return (
    <Box>
      <Box>{earningMechanismSummaryComponent}</Box>
      <Box>{awardSummary(selectedAward, awardConfiguration)}</Box>
    </Box>
  );
};
